import React, { useState } from 'react';
import api from '../../api';
import { useAuthContext } from '../../hooks/UseAuthContext';
import { toast } from 'react-toastify';
import { FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import Loader from '../../Components/Loader/Loader';
import './consistentcharacter.css';

function CharacterStudio({ onComplete }) {
  const { user } = useAuthContext();
  const [step, setStep] = useState('input'); // input | preview | testing | complete
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    character_descriptor: '',
    style_lock: '',
    generator: 'whisk',
  });

  const [variations, setVariations] = useState([]);
  const [tempId, setTempId] = useState('');
  const [selectedVariationId, setSelectedVariationId] = useState(null);
  const [selectedMediaId, setSelectedMediaId] = useState(null);

  const [testResults, setTestResults] = useState({
    character_id: '',
    base_image_url: '',
    test_images: {},
  });

  // ── STEP 1: Generate Variations ────────────────────────────────────────
  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.character_descriptor || !formData.style_lock) {
      toast.error('All fields are required');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/characters/generate', formData, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      setVariations(`${process.env.REACT_APP_MEDIA_URL}`+res.data.variations);
      setTempId(res.data.temp_id);
      setStep('preview');
      toast.success('Variations generated! Select one.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  // ── STEP 2: Save & Test ─────────────────────────────────────────────────
  const handleSaveAndTest = async () => {
    if (!selectedVariationId) {
      toast.error('Please select a variation');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        character_descriptor: formData.character_descriptor,
        style_lock: formData.style_lock,
        selected_variation_id: selectedVariationId,
        temp_id: tempId,
        media_id: selectedMediaId,
        generator: formData.generator,
      };

      const res = await api.post('/characters/save-and-test', payload, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      setTestResults(res.data);
      setStep('testing');
      toast.success('Character saved! Review test refinements.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  // ── STEP 3: Approve ─────────────────────────────────────────────────────
  const handleApprove = async () => {
    setLoading(true);
    try {
      await api.post(`/characters/${testResults.character_id}/approve`, {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      toast.success('Character approved!');
      setStep('complete');
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Approval failed');
    } finally {
      setLoading(false);
    }
  };

  // ── STEP 3: Reject ──────────────────────────────────────────────────────
  const handleReject = async () => {
    setLoading(true);
    try {
      await api.delete(`/characters/${testResults.character_id}/reject`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      toast.info('Character rejected. Try again.');
      setStep('input');
      setVariations([]);
      setTestResults({});
      setSelectedVariationId(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Rejection failed');
    } finally {
      setLoading(false);
    }
  };

  // ── RENDER ──────────────────────────────────────────────────────────────
  return (
    <div className="character-studio">
      {loading && <Loader />}

      {/* STEP 1: Input Form */}
      {step === 'input' && (
        <form className="studio-form" onSubmit={handleGenerate}>
          <div className="form-section">
            <h3>Create Your Character</h3>

            <div className="form-row">
              <label>Character Name</label>
              <input
                type="text"
                placeholder="e.g. Fitness Coach Mike"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="form-row">
              <label>Character Description</label>
              <textarea
                rows={4}
                placeholder="Describe appearance: face, hair, clothing, build..."
                value={formData.character_descriptor}
                onChange={(e) => setFormData({ ...formData, character_descriptor: e.target.value })}
              />
            </div>

            <div className="form-row">
              <label>Style Lock</label>
              <textarea
                rows={3}
                placeholder="Art style: e.g. Chalk oil painting, dramatic lighting..."
                value={formData.style_lock}
                onChange={(e) => setFormData({ ...formData, style_lock: e.target.value })}
              />
            </div>

            <div className="form-row">
              <label>Generator</label>
              <select
                value={formData.generator}
                onChange={(e) => setFormData({ ...formData, generator: e.target.value })}
              >
                <option value="whisk">Whisk</option>
                <option value="imagefx">ImageFX</option>
              </select>
            </div>

            <button type="submit" className="primary-btn" disabled={loading}>
              Generate 4 Variations
            </button>
          </div>
        </form>
      )}

      {/* STEP 2: Preview Variations */}
      {step === 'preview' && (
        <div className="variations-preview">
          <h3>Select Your Character</h3>
          <div className="variations-grid">
            {variations.map((variant) => (
              <div
                key={variant.variation_id}
                className={`variation-card ${selectedVariationId === variant.variation_id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedVariationId(variant.variation_id);
                  setSelectedMediaId(variant.media_id);
                }}
              >
                <img src={variant.preview_url} alt="Variation" />
                {selectedVariationId === variant.variation_id && (
                  <div className="selected-badge">
                    <FaCheck />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="button-group">
            <button className="secondary-btn" onClick={() => setStep('input')}>
              Back
            </button>
            <button className="primary-btn" onClick={handleSaveAndTest} disabled={!selectedVariationId || loading}>
              Save & Test
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Test Results */}
      {step === 'testing' && (
        <div className="test-results">
          <h3>Review Test Refinements</h3>
          <p className="test-description">
            These show how your character adapts to different poses. Check for consistency.
          </p>

          <div className="test-grid">
            <div className="test-item">
              <img src={`${process.env.REACT_APP_MEDIA_URL}`+testResults.base_image_url} alt="Base" />
              <span className="test-label">Base</span>
            </div>
            {testResults.test_images?.sitting && (
              <div className="test-item">
                <img src={`${process.env.REACT_APP_MEDIA_URL}`+testResults.test_images.sitting} alt="Sitting" />
                <span className="test-label">Sitting</span>
              </div>
            )}
            {testResults.test_images?.pointing && (
              <div className="test-item">
                <img src={`${process.env.REACT_APP_MEDIA_URL}`+testResults.test_images.pointing} alt="Pointing" />
                <span className="test-label">Pointing</span>
              </div>
            )}
            {testResults.test_images?.holding && (
              <div className="test-item">
                <img src={`${process.env.REACT_APP_MEDIA_URL}`+testResults.test_images.holding} alt="Holding" />
                <span className="test-label">Holding</span>
              </div>
            )}
          </div>

          <div className="button-group">
            <button className="reject-btn" onClick={handleReject} disabled={loading}>
              <FaTimes /> Reject
            </button>
            <button className="approve-btn" onClick={handleApprove} disabled={loading}>
              <FaCheck /> Approve
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: Complete */}
      {step === 'complete' && (
        <div className="complete-screen">
          <div className="success-icon">
            <FaCheck />
          </div>
          <h3>Character Created!</h3>
          <p>Your character is ready for production.</p>
        </div>
      )}
    </div>
  );
}

export default CharacterStudio;